import { Injector, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'

import { Store } from '@ngrx/store'

import { debounceTime, filter, map, tap } from 'rxjs'

import { FlxModalRef, FlxModalService } from '@flowx/angular-ui-toolkit'

import { ResourceHistoryParams } from './resource-history.service'

import { FlxResourceHistoryModalComponent } from '../components/resource-history/resource-history-modal.component'
import { selectQueryParam } from '../store/selectors/router.selectors'

export class ResourceHistoryModalService {
  #modalService = inject(FlxModalService)
  #store = inject(Store)
  #router = inject(Router)
  #modalInstance: FlxModalRef | null = null
  #injector = inject(Injector)

  constructor() {
    this.#store
      .select(selectQueryParam('resourceHistory'))
      .pipe(
        filter((resourceHistory) => !!resourceHistory),
        map((resourceHistory) => JSON.parse(resourceHistory as string)),
        tap((resourceHistory) => this.#openResourceHistoryModal(resourceHistory)),
        takeUntilDestroyed()
      )
      .subscribe()

    this.#store
      .select(selectQueryParam('graphOpen'))
      .pipe(
        debounceTime(0),
        filter((graphOpen) => graphOpen && !!this.#modalInstance),
        tap(() => {
          this.#modalService.hide()
          this.#modalInstance = null
        }),
        takeUntilDestroyed()
      )
      .subscribe()
  }

  async toggleResourceHistoryModal(
    params: ResourceHistoryParams | null,
    _interactionSource?: 'pageAction' | 'rowAction'
  ): Promise<void> {
    void _interactionSource
    // `history` is tracked on the History menu click (ResourceHistoryDefaultMenuItem), not here.
    await this.#router.navigate([], {
      queryParams: {
        resourceHistory: params ? JSON.stringify(params) : null,
      },
      queryParamsHandling: 'merge',
    })
  }

  #openResourceHistoryModal(params: ResourceHistoryParams): void {
    this.#modalInstance = this.#modalService.show(
      FlxResourceHistoryModalComponent,
      {
        closeOnBackdropClick: false,
        closeOnEscapeKey: true,
        backdropClass: 'w-full h-[calc(100%-56px)] top-[56px]',
        panelClass: 'w-full h-[calc(100%-76px)]',
        hostClass: 'items-end',
      },
      {
        ...params,
      },
      this.#injector
    )
    this.#modalInstance.componentRef.onDestroy(() => {
      this.#router.navigate([], {
        queryParams: {
          resourceHistory: null,
        },
        queryParamsHandling: 'merge',
      })
    })
  }
}
